import dataLayerPushEvent from 'utils/dataLayerPushEvent'
import Environment from 'entities/Environment'
import {
  getFullStorySessionStarted,
  setFullStorySessionStarted,
  getConversationFlow,
} from 'utils/sessionStorage'
import { sentryLog } from 'utils/sentryLogging'
import { getGlobalConfig } from 'utils/getTenantSpecificValues'
import type Response from 'entities/Response'
import type { FullStoryTrackingParameters } from 'entities/Message'

export type FullStoryEvents =
  | 'chatbot_started'
  | 'chatbot_login'
  | 'chatbot_order_selected'
  | 'cs_thread_finished'
  | 'cs_thread_rating'
  | 'chatbot_intent_selected'
  | 'chatbot_referral'

// Higher-order function to wrap tracking functions with fullstoryEnabled check
const createFullstoryFunction = <T extends (...args: Array<any>) => any>(
  fn: T
): T =>
  ((...args: Parameters<T>): ReturnType<T> | undefined => {
    const globalConfig = getGlobalConfig()
    const fullstoryEnabled = globalConfig?.fullstoryEnabled
    if (!fullstoryEnabled) {
      return undefined
    }
    return fn(...args)
  }) as T

export const formatDateTime = (date: Date): string =>
  date.toISOString().replace('T', ' ').slice(0, 19)

const toSnakeCase = (s: string): string =>
  s
    .split(/(?=[A-Z])/)
    .join('_')
    .toLowerCase()

export const pushFullStoryTracking = createFullstoryFunction(
  ({
    fsEventName,
    fsEventData,
  }: {
    fsEventName: FullStoryEvents
    fsEventData: Record<string, string | number | boolean>
  }) => {
    const trackingData = {
      fs_event_name: fsEventName,
      fs_event_data: fsEventData,
    }

    if (process.env.NODE_ENV !== Environment.production) {
      // eslint-disable-next-line no-console
      console.log(`STAGING: FullStory event was fired:`, trackingData)
      return
    }

    dataLayerPushEvent('fs_event', trackingData)
  }
)

export const trackFullStoryChatbotStarted = createFullstoryFunction(() => {
  const sessionStarted = getFullStorySessionStarted()

  if (!sessionStarted) {
    setFullStorySessionStarted(true)
  }

  pushFullStoryTracking({
    fsEventName: 'chatbot_started',
    fsEventData: {
      started_at: formatDateTime(new Date()),
      first_time_for_session: !sessionStarted,
    },
  })
})

export const trackFullStoryChatbotLogin = createFullstoryFunction(
  ({ success, wrongPass }: { success: boolean; wrongPass: boolean }) => {
    pushFullStoryTracking({
      fsEventName: 'chatbot_login',
      fsEventData: {
        started_at: formatDateTime(new Date()),
        success,
        wrong_pass: wrongPass,
      },
    })
  }
)

export const trackFullStoryCsThreadFinished = createFullstoryFunction(() => {
  pushFullStoryTracking({
    fsEventName: 'cs_thread_finished',
    fsEventData: {
      finished_at: formatDateTime(new Date()),
    },
  })
})

export const trackFullStoryOrderSelected = createFullstoryFunction(
  ({ orderType }: { orderType: string }) => {
    pushFullStoryTracking({
      fsEventName: 'chatbot_order_selected',
      fsEventData: {
        order_type: orderType,
      },
    })
  }
)

export const trackFullStoryRating = createFullstoryFunction(
  ({ properties }: FullStoryTrackingParameters) => {
    pushFullStoryTracking({
      fsEventName: 'cs_thread_rating',
      fsEventData: properties,
    })
  }
)

export const trackFullStoryReferral = createFullstoryFunction(
  ({
    destination,
    destinationDetails,
  }: {
    destination: string
    destinationDetails: string
  }) => {
    const intent = getConversationFlow()

    if (!intent) {
      sentryLog(new Error('No intent found for referral tracking'), {
        intent,
        destination,
        destinationDetails,
      })

      return
    }

    pushFullStoryTracking({
      fsEventName: 'chatbot_referral',
      fsEventData: {
        intent,
        destination,
        destination_details: destinationDetails,
      },
    })
  }
)

export const initFullStoryTracking = createFullstoryFunction(
  (responses: Array<Response>) => {
    if (!Array.isArray(responses)) {
      // eslint-disable-next-line no-console
      console.error(`Invalid response: `, responses)

      return
    }

    responses.forEach(({ payload }) => {
      const fsTracking = payload?.parameters?.fsTracking

      if (!fsTracking?.properties) {
        return
      }

      const fsEventData = Object.entries(fsTracking.properties).reduce(
        (acc, [key, value]) => {
          let processedValue: any

          if (
            key === 'startedAt' ||
            key === 'finishedAt' ||
            key === 'selectedAt'
          ) {
            processedValue = formatDateTime(new Date())
          } else {
            processedValue = value
          }

          // @ts-expect-error — using dynamic snake_case string key
          acc[toSnakeCase(key)] = processedValue

          return acc
        },
        {}
      )

      pushFullStoryTracking({
        fsEventName: fsTracking.name as FullStoryEvents,
        fsEventData,
      })
    })
  }
)
